import {useIntl} from 'react-intl';
import React, {useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import ReactSelect, {components, OptionProps} from 'react-select';
import {ListOrder, OrderStatus} from '../../../interfaces/order';
import {createSearchParams, Link, useNavigate} from 'react-router-dom';
import {formatDate} from '../../../common/tools/date';
import {useQuery} from 'react-query';
import {getCollectInfos, getOrdersByClient} from '../api';
import TableMetronic from '../../../common/components/table/TableMetronic';
import SeeOrderTrackingConversationIcon from '../mail-history/ticket-messages/SeeOrderTrackingConversationIcon';
import {useGetMe} from '../../../hooks/useGetMe';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import EtatBadge from '../../../../Domains/Service/Components/EtatBadge';
import {getOrderStatusColor, getOrderStatusLabel} from './orderStatusHelper';
import styles from './Orders.module.scss';
import clsx from 'clsx';
import ECV1Link from '../../../common/components/ECV1Link';

interface FilterOption {
  value: string;
  label: string;
  count: number;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

interface OrdersProps {
  status: OrderStatus;
  limit?: number;
  withSearchBar?: boolean;
  customLinkTitle?: string;
  customLinkRoute?: string;
  hideIfEmpty?: boolean;
}

const Orders = ({
  status,
  customLinkTitle,
  customLinkRoute,
  limit = 0,
  withSearchBar = true,
  hideIfEmpty = false,
}: OrdersProps): JSX.Element => {
  const intl = useIntl();
  const customer = useGetMe();
  const navigate = useNavigate();
  const [selectedServices, setSelectedServices] = useState<FilterOption[]>([]);
  const [selectedSites, setSelectedSites] = useState<FilterOption[]>([]);

  const {data: collectInfos} = useQuery<string[]>(['collectInfos', customer], async () => {
    if (customer) {
      return getCollectInfos(customer.id.toString());
    }
    return [];
  });

  const {isLoading, data: orders} = useQuery<ListOrder[]>(
    ['get-orders-by-client', status],
    async () => getOrdersByClient(status)
  );

  const serviceOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, FilterOption>();
    (orders ?? []).forEach((o) => {
      if (!o.service) return;
      const existing = map.get(o.service);
      if (existing) {
        existing.count += 1;
        return;
      }
      const displayId = o.serviceId ?? o.id;
      map.set(o.service, {value: o.service, label: `${displayId} - ${o.service}`, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [orders]);

  const siteOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, FilterOption>();
    (orders ?? []).forEach((o) => {
      if (!o.site) return;
      const existing = map.get(o.site);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = o.siteId ? `${o.siteId} - ${o.site}` : o.site;
      map.set(o.site, {value: o.site, label, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!orders) return orders;
    if (selectedServices.length === 0 && selectedSites.length === 0) return orders;
    const serviceValues = new Set(selectedServices.map((o) => o.value));
    const siteValues = new Set(selectedSites.map((o) => o.value));
    return orders.filter((o) => {
      const serviceOk =
        serviceValues.size === 0 || (o.service != null && serviceValues.has(o.service));
      const siteOk = siteValues.size === 0 || (o.site != null && siteValues.has(o.site));
      return serviceOk && siteOk;
    });
  }, [orders, selectedServices, selectedSites]);

  const CheckboxOption = (props: OptionProps<FilterOption, true>) => (
    <components.Option {...props}>
      <div className="d-flex align-items-start">
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          className="me-2 mt-1 flex-shrink-0"
        />
        <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
          {props.label}
        </label>
        {props.data.count != null && (
          <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
        )}
      </div>
    </components.Option>
  );

  const columns = useMemo<ColumnDef<ListOrder>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.ID'}),
        accessorKey: 'id',
        size: 110,
        cell: (info) => info.getValue<string | null>(),
      },
      {
        header: intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.SERVICE'}),
        accessorKey: 'service',
        cell: (info) => info.getValue<string | null>(),
      },
      {
        header: intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.SITE'}),
        accessorKey: 'site',
        size: 280,
        cell: (info) => info.getValue<string | null>() ?? '-',
      },
      {
        header: intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.STATUS'}),
        accessorKey: 'status',
        size: 260,
        cell: (info) => {
          const status = info.getValue<string | null>();
          return status ? (
            <EtatBadge
              label={getOrderStatusLabel(status, intl)}
              color={getOrderStatusColor(status)}
            />
          ) : (
            '-'
          );
        },
      },
      {
        header: intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.OPENING'}),
        accessorKey: 'opening',
        size: 100,
        cell: (info) => formatDate(info.getValue<string | null>()),
      },
      {
        header: intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.ACTION'}),
        accessorKey: 'mails',
        enableSorting: false,
        size: 100,
        meta: {noClick: true},
        cell: (info) => {
          const searchParams = new URLSearchParams();

          searchParams.append('orderId', info.row.original.id);
          if (info.row.original.siteId) {
            searchParams.append('site', info.row.original.siteId.toString());
          }

          return (
            <>
              <div style={{width: '100px'}}>
                <SeeOrderTrackingConversationIcon
                  orderId={info.row.original.id}
                  collectInfos={collectInfos}
                />
                {status === 1 && (
                  <span
                    role="button"
                    style={{marginLeft: 10, display: 'inline-block'}}
                    onClick={() => {
                      navigate({
                        pathname: `/support`,
                        search: createSearchParams(searchParams).toString(),
                      });
                    }}
                  >
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          <span>{intl.formatMessage({id: 'ORDERS.NEW_REQUEST'})}</span>
                        </Tooltip>
                      }
                      placement={'top'}
                    >
                      <i className="fs-1 fa-solid fa-file-circle-exclamation"></i>
                    </OverlayTrigger>
                  </span>
                )}
              </div>
            </>
          );
        },
      },
    ],
    [intl, collectInfos, navigate, status]
  );

  const renderMobileCard = (order: ListOrder): JSX.Element => {
    const searchParams = new URLSearchParams();
    searchParams.append('orderId', order.id);
    if (order.siteId) {
      searchParams.append('site', order.siteId.toString());
    }

    return (
      <div className={styles.mobileCardBody}>
        <div className={styles.mobileCardHeader}>
          <span className={styles.mobileCardId}>{order.id}</span>
          {order.status && (
            <EtatBadge
              label={getOrderStatusLabel(order.status, intl)}
              color={getOrderStatusColor(order.status)}
            />
          )}
        </div>
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>
            {intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.SERVICE'})}
          </span>
          <span>{order.service ?? '-'}</span>
        </div>
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>
            {intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.SITE'})}
          </span>
          <span className={styles.mobileCardValue}>{order.site ?? '-'}</span>
        </div>
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>
            {intl.formatMessage({id: 'ORDER_TRACKING.IN_PROGRESS.TABLE.OPENING'})}
          </span>
          <span>{formatDate(order.opening)}</span>
        </div>
        <div className={styles.mobileCardActions} onClick={(e) => e.stopPropagation()}>
          {collectInfos?.includes(order.id) && (
            <ECV1Link
              path={'collecteinformation/listingforms'}
              className={clsx('btn btn-sm btn-light-warning', styles.mobileActionButton)}
            >
              <>
                <i className="fa-solid fa-triangle-exclamation fs-4 me-2"></i>
                {intl.formatMessage({id: 'ORDERS.CONVERSATION.COLLECTION'})}
              </>
            </ECV1Link>
          )}
          <Link
            to={`/orders/${order.id}#tracking-messages`}
            className={clsx('btn btn-sm btn-light-primary', styles.mobileActionButton)}
          >
            <i className="fa-solid fa-envelopes-bulk fs-4 me-2"></i>
            {intl.formatMessage({id: 'ORDER_TRACKING.SHOW_CONVERSATION'})}
          </Link>
          {status === 1 && (
            <button
              type="button"
              className={clsx('btn btn-sm btn-light-danger', styles.mobileActionButton)}
              onClick={() =>
                navigate({
                  pathname: `/support`,
                  search: createSearchParams(searchParams).toString(),
                })
              }
            >
              <i className="fa-solid fa-file-circle-exclamation fs-4 me-2"></i>
              {intl.formatMessage({id: 'ORDERS.NEW_REQUEST'})}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (hideIfEmpty && (!orders || orders.length === 0)) {
    return <div></div>;
  }

  const dataToDisplay = filteredOrders ?? orders;

  const filtersToolbar = (
    <>
      <div className={styles.filterSelect}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={serviceOptions}
          value={selectedServices}
          onChange={(vals) => setSelectedServices(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'ORDER_TRACKING.FILTER.ALL_SERVICES'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div className={styles.filterSelectSite}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={siteOptions}
          value={selectedSites}
          onChange={(vals) => setSelectedSites(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_SITES'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
    </>
  );

  return (
    <>
      <TableMetronic<ListOrder>
        title={intl.formatMessage({id: 'MENU.MY_ORDERS'})}
        toolbar={filtersToolbar}
        stackedHeader
        data={
          (limit && limit > 0
            ? dataToDisplay?.slice(dataToDisplay.length - limit, dataToDisplay.length)
            : dataToDisplay) ?? []
        }
        columns={columns}
        renderMobileCard={renderMobileCard}
        isLoading={isLoading}
        defaultSorting={[
          {
            id: 'opening',
            desc: true,
          },
        ]}
        searchBar={withSearchBar}
        pagination={withSearchBar}
        customLinkTitle={customLinkTitle}
        customLinkRoute={customLinkRoute}
        rowClickable={(row) => !!row.id} // cliquable si l'ID existe
        onRowClick={(row) => navigate(`/orders/${row.id}`)}
      />
    </>
  );
};

export default Orders;
